import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectResult } from '../../components/select/select-chain.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { Grid, Paper, Typography } from '@mui/material';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { SiTrackPage } from '../../components/si-track-page';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { UserPermissionName } from '../../models/user-permission-name';
import { BillingReport } from '../../models/billing-report';
import { billingReportColumns } from './billing-report-columns';
import { BillingFilters } from './billing.filters';
import { billingReportApiKeyStore } from '../../api/billing-report-api-key-store';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
  field: string;
}
interface CellProps {
  workorder: any;
}

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};

export const BillingReportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [hovered, setHovered] = useState(false);

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      user: { userID: '', fullName: '' },
      service: { serviceID: '', serviceName: '' },
      dateCriteria: { dateCriteriaValue: '', dateCriteria: '' },
      workOrderStatus: { workOrderStatusID: '', workOrderStatus: '' },
      action: '',
      workorder: { workOrderID: 0, workorderNumber: '' },
      requestedFromDUX: false,
    },
  });

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
    billingReportApiKeyStore.getBillingReports(selectedFilters),
    !!selectedFilters?.customerID ||
    !!selectedFilters?.officeID ||
    !!selectedFilters?.projectID ||
    !!selectedFilters?.unitID ||
    !!selectedFilters?.serviceID ||
    !!selectedFilters?.inspectorID ||
    !!selectedFilters?.wOStatusID ||
    !!selectedFilters?.dateFrom ||
    !!selectedFilters?.dateTo ||
    !!selectedFilters?.dateCriteria ||
    !!selectedFilters?.showAll ||
    !!selectedFilters?.requestedFromDUX ||
    !!selectedFilters?.workorder
    // || !!selectedFilters?.scheduledDateFrom ||
    // !!selectedFilters?.scheduledDateTo
  );

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      showAll: filtersData?.action ?? '',
      customerID: filtersData?.customer?.customerID ?? 0,
      officeID: filtersData?.office?.officeID ?? 0,
      projectID: filtersData?.project?.projectID ?? 0,
      unitID: filtersData?.unit?.unitID ?? 0,
      serviceID: filtersData?.service?.serviceID ?? 0,
      // inspectorID: filtersData?.user?.userID ?? 0,
      // wOStatusID: filtersData?.workOrderStatus?.workOrderStatusID ?? 0,
      dateFrom: filtersData?.fromDate ?? '',
      dateTo: filtersData?.toDate ?? '',
      // dateCriteria: filtersData?.dateCriteria?.dateCriteriaValue ?? '',
      workorder: filtersData?.workorder?.workorderNumber ?? 0,
      requestedFromDUX: filtersData?.requestedFromDUX,
      // scheduledDateFrom: filtersData?.scheduledDateFrom ?? '',
      // scheduledDateTo: filtersData?.scheduledDateTo ?? '',
    };
    // setSelectedFilters(filteringData);
    // refetch();
    const prev = selectedFilters;

    const isSame =
      prev &&
      prev.showAll === filteringData.showAll &&
      prev.customerID === filteringData.customerID &&
      prev.officeID === filteringData.officeID &&
      prev.projectID === filteringData.projectID &&
      prev.unitID === filteringData.unitID &&
      prev.serviceID === filteringData.serviceID &&
      // prev.inspectorID === filteringData.inspectorID &&
      // prev.wOStatusID === filteringData.wOStatusID &&
      prev.dateFrom === filteringData.dateFrom &&
      prev.dateTo === filteringData.dateTo &&
      // prev.dateCriteria === filteringData.dateCriteria &&
      prev.workorder === filteringData.workorder &&
      prev.requestedFromDUX === filteringData.requestedFromDUX;
    // prev.scheduledDateFrom === filteringData.scheduledDateFrom &&
    // prev.scheduledDateTo === filteringData.scheduledDateTo;

    if (isSame) {
      refetch(); // No change, just refetch
    } else {
      setSelectedFilters(filteringData); // Update and refetch will be triggered via hook
    }
  };

  const handleShowAll = () => {
    form.handleSubmit(handleSubmit)();
  };

  const WorkOrderCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickWorkorderNumber = () => {
      navigate(`/workorders?searchType=workorders&searchText=${workorder?.workOrderNumber}`);
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickWorkorderNumber}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.workOrderNumber}
        </Typography>
      </td>
    );
  };

  const BillingAmountCell = ({ workorder }: CellProps) => {
    const billingAmount = workorder?.billingAmountNumeric;

    const formattedAmount =
      billingAmount !== undefined && billingAmount !== null
        ? `$${parseFloat(billingAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if billingAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'workOrderNumber') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <WorkOrderCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'billingAmountNumeric') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <BillingAmountCell workorder={dataItem.dataItem} />;
      };
    }

    return undefined;
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('BillingReport', 'BillingReport'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredBillingReports, setFilteredBillingReports] = useState<BillingReport[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredBillingReportList = data?.data?.filter((BillingReport: any) =>
      Object.values(BillingReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredBillingReports(filteredBillingReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'BillingReport',
      pageName: 'BillingReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'BillingReport',
      pageName: 'BillingReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage title="Billing Reports">
      <ReactHookForm formName="BILLING_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <BillingFilters onShowAllWorkOrder={handleShowAll} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='billing_report'
                columns={billingReportColumns}
                dataItemKey="workOrderID"
                // viewRoute={(value) => `/workorders?searchType=workorders&searchText=${value?.workOrderNumber}`}
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                isBusy={isLoading || isFetching || isLoadingGridLayout}
                // -----------------Grid Layout-------
                dataItems={filteredBillingReports || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
    </SiTrackPage>
  );
};
